import React, { useState } from 'react';
import {
  PasswordTextForm,
  GenericForm,
  Button as ButtonBase,
  Card,
  Link,
  Checklist,
} from '@components';
import styled from 'styled-components';

import CreatePasswordSchema from '@schemas/CreatePasswordSchema';
import { useTranslation, Trans } from 'react-i18next';
import { CustomCheckbox } from 'components/Forms/Checkbox';
import { validatePassword } from '../../helpers/passwordForm';

const Button = styled(ButtonBase)`
  width: fit-content !important;
  border-radius: 36px !important;
  ${({ theme }) => theme.breakpoint('md')} {
    width: fit-content;
    display: block;
    margin-left: auto;
    border-radius: 36px !important;
  }
`;

const CheckBoxTyc = styled.div`
  display: ${(props) => props.display};
  justify-content: flex-start;
  align-items: ${(props) => (props.isPeCL ? 'flex-start' : 'center')};
`;

const CointainerButton = styled.div`
  margin: auto;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const ContainerCheckList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100% !important;
  margin-bottom: 16px;
  justify-content: flex-start;
  align-items: flex-start;
`;

export const RePasswordForm = ({
  title,
  subtitle,
  handleSubmit,
  errorEndpoint,
  handleFirstInputClick,
  handleSecondInputClick,
  state,
  showCheckPrivacyPolicy,
  viewTyc,
  showPolicies,
  isCreate,
}) => {
  const { t } = useTranslation();

  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [checkedTyC, setCheckedTyC] = useState(false);
  const [checkedPP, setCheckedPP] = useState(false);

  const {
    lengthStatus,
    upperCaseStatus,
    lowerCaseStatus,
    specialCharStatus,
    numberStatus,
    totalValid,
  } = validatePassword(password, state.clickedSecondInput);

  const errorInput =
    state.clickedSecondInput === true &&
    (lengthStatus === 'error' ||
      upperCaseStatus === 'error' ||
      lowerCaseStatus === 'error' ||
      specialCharStatus === 'error' ||
      numberStatus === 'error');

  const handleChangeCheck = (e) => {
    const { target } = e;
    if (target.id === 'privacy_policy' && showCheckPrivacyPolicy) {
      setCheckedPP((prev) => !prev);
    } else {
      setCheckedTyC((prev) => !prev);
    }
  };

  const matchPasswords = password === repeatPassword;

  return (
    <Card isPassForm title={title} subtitle={subtitle} width="460px" isResetPass>
      <GenericForm
        schema={CreatePasswordSchema}
        item
        xs={12}
        onSubmit={() => handleSubmit(password)}
        container
        autocomplete="off"
        style={{ justifyContent: 'flex-start', width: '100%', flexDirection: 'column' }}
      >
        <PasswordTextForm
          value={password}
          fullWidth
          variant="outlined"
          label={t('passwordForms.pass')}
          gridProps={{ item: true, xs: 12 }}
          handleChange={(e) => setPassword(e.target.value)}
          id="password"
          inputProps={{
            autocomplete: 'new-password',
            form: {
              autocomplete: 'off',
            },
          }}
          error={errorInput || errorEndpoint}
          helperText={
            (errorInput || errorEndpoint) &&
            t(`passwordForms.helperText.${errorInput ? 'errorReview' : 'errorEndpoint'}`)
          }
          onFocus={handleFirstInputClick}
        />
        <PasswordTextForm
          fullWidth
          isLoginForm
          value={repeatPassword}
          variant="outlined"
          label={t('passwordForms.passChange')}
          gridProps={{ item: true, xs: 12 }}
          handleChange={(e) => setRepeatPassword(e.target.value)}
          id="repeatPassword"
          inputProps={{
            autocomplete: 'new-password',
            form: {
              autocomplete: 'off',
            },
          }}
          onFocus={handleSecondInputClick}
          error={state.clickedSecondInput === true && repeatPassword.length > 0 && !matchPasswords}
          helperText={
            state.clickedSecondInput === true &&
            repeatPassword.length > 0 &&
            !matchPasswords &&
            t('passwordForms.machPass')
          }
        />
        <ContainerCheckList>
          <Checklist text={t('passwordForms.eightCharacters')} status={lengthStatus} />
          <Checklist text={t('passwordForms.upper')} status={upperCaseStatus} />
          <Checklist text={t('passwordForms.min')} status={lowerCaseStatus} />
          <Checklist
            text={<Trans i18nKey="passwordForms.specialChar" components={{ bold: <b /> }} />}
            status={specialCharStatus}
          />
          <Checklist text={t('passwordForms.oneNumber')} status={numberStatus} />
        </ContainerCheckList>
        {showCheckPrivacyPolicy ? (
          <>
            <CheckBoxTyc
              gridProps={{ item: true, xs: 12 }}
              display={(viewTyc === false && 'none') || 'flex'}
            >
              <CustomCheckbox checked={checkedTyC} handleChange={handleChangeCheck} id="tyc" />
              <p style={{ paddingTop: '3px' }}>
                {t('Onboarding.MerchantOnboarding.passwordForm.checkText')}
                <Link
                  tag="tyc"
                  style={{
                    cursor: 'pointer',
                    color: 'var(--grass)',
                  }}
                  target="_blank"
                  href={t('common.termsOfService.url')}
                  id="onb-createPass-tyc"
                >
                  {t('passwordForms.terms')}
                </Link>
              </p>
            </CheckBoxTyc>
            <CheckBoxTyc
              gridProps={{ item: true, xs: 12 }}
              display={(viewTyc === false && 'none') || 'flex'}
            >
              <CustomCheckbox
                checked={checkedPP}
                handleChange={handleChangeCheck}
                id="privacy_policy"
              />
              <p style={{ paddingTop: '3px' }}>
                {t('Onboarding.MerchantOnboarding.passwordForm.checkTextPrivacyPolicy')}
                <Link
                  tag="privacy_policy"
                  style={{
                    cursor: 'pointer',
                    color: 'var(--grass)',
                  }}
                  target="_blank"
                  href={t('passwordForms.policy')}
                  id="onb-createPass-privacy_policy"
                >
                  {t('Onboarding.MerchantOnboarding.passwordForm.privacyPolicy')}
                </Link>
              </p>
            </CheckBoxTyc>
          </>
        ) : (
          <CheckBoxTyc
            gridProps={{ item: true, xs: 12 }}
            display={(viewTyc === false && 'none') || 'flex'}
            isPeCL
          >
            <CustomCheckbox checked={checkedTyC} handleChange={handleChangeCheck} id="tyc" />
            <p
              style={{
                paddingTop: '8px',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                margin: 'initial',
              }}
            >
              <span style={{ paddingRight: '4px' }}>{t('passwordForms.checkText')}</span>
              <Link
                tag="tyc"
                style={{
                  cursor: 'pointer',
                  color: 'var(--grass)',
                }}
                target="_blank"
                href={t('common.termsOfService.url')}
                id="onb-createPass-tyc"
              >
                <span style={{ paddingRight: '4px' }}>{t('passwordForms.terms')}</span>
              </Link>
              {showPolicies && (
                <>
                  {' '}
                  y las
                  <Link
                    tag="tyc"
                    style={{
                      cursor: 'pointer',
                      color: 'var(--grass)',
                    }}
                    target="_blank"
                    href={t('common.termsOfService.politicsPriv')}
                    id="onb-createPass-tyc"
                  >
                    {' '}
                    {t('passwordForms.policy')}
                  </Link>
                </>
              )}
            </p>
          </CheckBoxTyc>
        )}
        <CointainerButton>
          <Button
            disabled={
              (!checkedTyC && viewTyc !== false) ||
              (!checkedPP && viewTyc !== false && showCheckPrivacyPolicy) ||
              !totalValid ||
              !matchPasswords
            }
            gridProps={{ xs: 12, item: true }}
            type="submit"
          >
            {isCreate ? t('passwordForms.buttonCreate') : t('passwordForms.button')}
          </Button>
        </CointainerButton>
      </GenericForm>
    </Card>
  );
};

export default RePasswordForm;
